// 하단 — 섹터 sparkline 그리드 (P0, PRD §5.7.2 권위).
// 레이아웃: 테이블 (행=섹터, 열=섹터명/breadth/peak/avg_score/stock_count).
//   1열: 섹터명 (swatch + 이름 + 최근 score)
//   2~5열: 4 sparkline (각 60거래일)
// 행 클릭 → 우측 드로어 (raw row 표 + CSV) [P0].
// 입력: mode=daily_top3 & include_sparkline=true 응답.

import { memo, useMemo, useState } from 'react';
import type { SectorHistoryRow } from './leadingSectors.types';
import { sectorColor } from './leadingSectors.types';
import { SectorDetailDrawer } from './SectorDetailDrawer';

interface Props {
  rows: SectorHistoryRow[];
  isLoading?: boolean;
}

interface SectorSeries {
  sector: string;
  color: string;
  rows: SectorHistoryRow[]; // 일자 ASC
}

type MetricKey = 'breadth' | 'peak' | 'avg_score' | 'stock_count';

const METRICS: { key: MetricKey; label: string; digits: number }[] = [
  { key: 'breadth', label: 'breadth', digits: 2 },
  { key: 'peak', label: 'peak', digits: 2 },
  { key: 'avg_score', label: 'avg score', digits: 2 },
  { key: 'stock_count', label: 'stock count', digits: 0 },
];

export const SectorSparklineGrid = memo(function SectorSparklineGrid({
  rows,
  isLoading,
}: Props): JSX.Element {
  const [selectedSector, setSelectedSector] = useState<string | null>(null);

  const series: SectorSeries[] = useMemo(() => {
    const bySector = new Map<string, SectorHistoryRow[]>();
    for (const r of rows) {
      const arr = bySector.get(r.sector_name) ?? [];
      arr.push(r);
      bySector.set(r.sector_name, arr);
    }
    return Array.from(bySector.entries())
      .map(([sector, list]) => {
        list.sort((a, b) => a.trading_date.localeCompare(b.trading_date));
        return { sector, color: sectorColor(sector), rows: list };
      })
      // 최근 일자의 score_daily 가 큰 순 — 운영자가 중요 섹터를 위에서 본다.
      .sort((a, b) => {
        const av = Number(a.rows[a.rows.length - 1]?.score_daily ?? 0);
        const bv = Number(b.rows[b.rows.length - 1]?.score_daily ?? 0);
        return bv - av;
      });
  }, [rows]);

  const selected = useMemo(
    () => (selectedSector ? series.find((s) => s.sector === selectedSector) ?? null : null),
    [series, selectedSector]
  );

  if (isLoading) {
    return <div className="ls-empty">불러오는 중…</div>;
  }

  if (series.length === 0) {
    return <div className="ls-empty">최근 기간에 Top3 진입한 섹터가 없습니다.</div>;
  }

  return (
    <>
      <table
        className="ls-sparkline-table"
        data-testid="ls-sparkline-grid"
      >
        <thead>
          <tr>
            <th className="ls-sparkline-table__sector-col">섹터</th>
            {METRICS.map((m) => (
              <th key={m.key} className="ls-sparkline-table__metric-col">
                {m.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map((s) => {
            const last = s.rows[s.rows.length - 1];
            return (
              <tr
                key={s.sector}
                className="ls-sparkline-table__row"
                data-testid={`ls-sector-card-${s.sector}`}
                onClick={() => setSelectedSector(s.sector)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedSector(s.sector);
                  }
                }}
              >
                <td className="ls-sparkline-table__sector">
                  <span
                    className="ls-sector-card__swatch"
                    style={{ background: s.color }}
                    aria-hidden
                  />
                  <span className="ls-sector-card__name">{s.sector}</span>
                  <span className="ls-sector-card__meta">
                    {s.rows.length}거래일
                    {last?.score_daily != null
                      ? ` · 최근 score ${Number(last.score_daily).toFixed(2)}`
                      : ''}
                  </span>
                </td>
                {METRICS.map((m) => (
                  <td key={m.key} className="ls-sparkline-table__metric">
                    <Sparkline
                      color={s.color}
                      digits={m.digits}
                      values={s.rows.map((r) => toNum(r[m.key]))}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <SectorDetailDrawer
        open={selected !== null}
        sector={selected?.sector ?? null}
        color={selected?.color ?? 'var(--ink-faint)'}
        rows={selected?.rows ?? []}
        onClose={() => setSelectedSector(null)}
      />
    </>
  );
});

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function Sparkline({
  color,
  digits,
  values,
}: {
  color: string;
  digits: number;
  values: (number | null)[];
}): JSX.Element {
  const W = 160;
  const H = 40;
  const PAD = 2;

  const clean = values.filter((v): v is number => v != null);
  const last = clean[clean.length - 1];

  const path = useMemo(() => {
    if (clean.length === 0) return '';
    const min = Math.min(...clean);
    const max = Math.max(...clean);
    const span = max - min || 1;
    const stepX = clean.length === 1 ? 0 : (W - PAD * 2) / (clean.length - 1);
    return clean
      .map((v, i) => {
        const x = PAD + i * stepX;
        const y = PAD + (H - PAD * 2) - ((v - min) / span) * (H - PAD * 2);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
  }, [clean]);

  return (
    <div className="ls-sparkline">
      <svg
        className="ls-sparkline__svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden
      >
        {clean.length > 0 ? (
          <path d={path} className="ls-sparkline__line" style={{ stroke: color }} />
        ) : (
          <text x={W / 2} y={H / 2} textAnchor="middle" fontSize={9} fill="var(--ink-muted)">
            데이터 없음
          </text>
        )}
      </svg>
      {last != null && (
        <span className="ls-sparkline__last">{last.toFixed(digits)}</span>
      )}
    </div>
  );
}
